import req from '../../api/request'

/**
 * The viewer's own rights on the organization. Read from the response `meta`,
 * not the organization record — the same source the Org screen uses.
 */
export interface OrganizationPermission {
  /** Gates the team and roster import/export, as on the Org screen's Teams tab. */
  update: boolean
}

/**
 * The organization fields callers rely on today, plus whatever else the API
 * returns. `sync_salesforce` is the reason this request exists — the
 * permission helpers in `add-roster-utils` consult it for the Canlan branch,
 * and the tree response does not carry it. The index signature lets future
 * callers read additional fields without widening this type themselves.
 */
export interface OrganizationDetails {
  id: string
  name: string
  name_full: string
  sync_salesforce: boolean
  /** Scopes the roster Add Player "Global Search" toggle on non-Canlan tenants. */
  account_id?: string
  permission: OrganizationPermission
  [key: string]: unknown
}

export const readOrganization = async (
  organizationId: string
): Promise<OrganizationDetails> => {
  const response = await req(`/organizations/${organizationId}`)
  const data = response?.data ?? {}
  // Keyed by the requested id, matching the Org screen's `useReadItem`.
  const permissions = response?.meta?.permissions?.[organizationId] ?? {}

  return {
    ...data,
    id: String(data.id ?? organizationId),
    // The API has returned 1/0 for this flag; coerce so callers get a boolean.
    sync_salesforce: Boolean(data.sync_salesforce),
    // Absent means no rights — the controls it gates err closed.
    permission: { update: Boolean(permissions.update) },
  }
}
